// =====================================================================
// TARPI — Line page (RUMAX / OVOMAX)
// Renders the line's full product catalog grouped by category
// Reads `data-line` attribute from #line-products mount
// =====================================================================

const { useSyncExternalStore: _lpSync, useState: _lpUseState, useEffect: _lpUseEffect } = React;

function _lpUseCart() {
  return _lpSync(
    (cb) => window.TARPI_store.subscribe(cb),
    () => window.TARPI_store.get(),
    () => window.TARPI_store.get()
  );
}
function _lpUseCompare() {
  return _lpSync(
    (cb) => window.TARPI_compare.subscribe(cb),
    () => window.TARPI_compare.get(),
    () => window.TARPI_compare.get()
  );
}

const VIEW_KEY = "tarpi_line_view";

function LineProductRow({ product, inCart, inCompare }) {
  const _lpHome = (window.tarpiData && window.tarpiData.homeUrl) || (location.origin + "/");
  const onOpen = () => window.location.href = `${_lpHome}product/${product.id}/`;
  const addCart = (e) => { e.stopPropagation(); window.TARPI_store.add(product); };
  const toggleCompare = (e) => { e.stopPropagation(); window.TARPI_compare.toggle(product.id); };

  return (
    <article className={`line-product-row line-${product.line}`}>
      <div className="lpr-bag" onClick={onOpen}>
        {product.imageThumb
          ? <img src={product.imageThumb} alt={product.name} className="lpr-photo" loading="lazy" />
          : <window.FeedBag line={product.line} name={product.name} category={product.categoryLabel} />
        }
      </div>
      <div className="lpr-info" onClick={onOpen}>
        <span className="lpr-cat">{product.categoryLabel}</span>
        <h4 className="lpr-name">{product.name}</h4>
        <p className="lpr-desc">{product.description}</p>
        <div className="lpr-stage">
          <span>{product.species}</span>
          <span className="dot">·</span>
          <span>{product.stage}</span>
        </div>
      </div>
      <div className="lpr-specs lpr-specs-hidden" onClick={onOpen}></div>
      <div className="lpr-actions">
        <button
          className={`lpr-btn lpr-btn-compare ${inCompare ? 'active' : ''}`}
          onClick={toggleCompare}
          title={inCompare ? "Quitar del comparador" : "Agregar al comparador"}
          aria-label={inCompare ? "Quitar del comparador" : "Agregar al comparador"}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
            <rect x="3" y="4" width="7" height="16" rx="1"/>
            <rect x="14" y="4" width="7" height="16" rx="1"/>
          </svg>
          <span>{inCompare ? "Comparando" : "Comparar"}</span>
        </button>
        <button
          className={`lpr-btn lpr-btn-add ${inCart ? 'active' : ''}`}
          onClick={addCart}
          title={inCart ? "En cotización" : "Agregar a cotización"}
        >
          {inCart ? "✓ En cotización" : "Cotizar +"}
        </button>
        <button className="lpr-btn lpr-btn-view" onClick={onOpen}>
          Ver detalle
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
            <path d="M5 12h14M13 6l6 6-6 6"/>
          </svg>
        </button>
      </div>
    </article>
  );
}

function LinePage({ line }) {
  const cart = _lpUseCart();
  const compare = _lpUseCompare();
  const [view, setView] = _lpUseState(() => {
    try { return localStorage.getItem(VIEW_KEY) || "grid"; } catch { return "grid"; }
  });
  _lpUseEffect(() => {
    try { localStorage.setItem(VIEW_KEY, view); } catch {}
  }, [view]);

  const products = window.TARPI_PRODUCTS.filter(p => p.line === line);
  const categories = window.TARPI_LINES[line].categories;

  const cartIds = new Set(cart.map(c => c.id));
  const compareIds = new Set(compare);

  return (
    <div className={`line-page-products view-${view}`}>
      {/* Global view toggle */}
      <div className="line-view-toolbar">
        <div className="line-view-toolbar-inner">
          <span className="line-view-label">Vista</span>
          <div className="line-view-toggle" role="tablist">
            <button
              className={`view-btn ${view === 'grid' ? 'active' : ''}`}
              onClick={() => setView('grid')}
              aria-label="Ver como grilla"
              title="Grilla"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="1"/>
                <rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/>
                <rect x="14" y="14" width="7" height="7" rx="1"/>
              </svg>
              <span>Grilla</span>
            </button>
            <button
              className={`view-btn ${view === 'list' ? 'active' : ''}`}
              onClick={() => setView('list')}
              aria-label="Ver como lista"
              title="Lista"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                <path d="M3 6h18M3 12h18M3 18h18"/>
              </svg>
              <span>Lista</span>
            </button>
          </div>
        </div>
      </div>

      {categories.map(catKey => {
        const cps = products.filter(p => p.category === catKey);
        if (cps.length === 0) return null;
        return (
          <section className={`line-cat-block line-${line}`} key={catKey} id={`cat-${catKey}`}>
            <header className="line-cat-head reveal">
              <span className="eyebrow">{cps.length} productos</span>
              <h3 className="display display-sm">{window.TARPI_CATEGORIES[catKey]}</h3>
            </header>
            <div className="line-products-list">
              {cps.map(p => (
                <LineProductRow
                  key={p.id}
                  product={p}
                  inCart={cartIds.has(p.id)}
                  inCompare={compareIds.has(p.id)}
                  view={view}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

// Mount based on data-line attribute on placeholder
const lpMount = document.getElementById("line-products");
if (lpMount) {
  const line = lpMount.dataset.line;
  ReactDOM.createRoot(lpMount).render(<LinePage line={line} />);
}
